import React, { useState } from 'react';
import VoxelPanel from '../../../components/voxel/Surfaces/VoxelPanel';
import { useFormik } from 'formik';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { faMinus, faPlus, faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import { usersApiService } from '../../../graphql/users/usersApiService';
import Capacity from './Capacity';

export interface LicenseSettingsProps {
  team: CurrentTeamGq;
  reload: () => Promise<unknown>;
}

export const LicenseSettings: React.FC<LicenseSettingsProps> = ({ reload, team }) => {
  const updateSettingsMutation = usersApiService.mutations.useUpdateSubscriptionLicenses();
  const formProps = useFormik({
    enableReinitialize: true,
    initialValues: {
      licenses: team.licenses,
    },
    validate: (values) => {
      const errors: any = {};

      const licenseIssues: string[] = [];
      let requiredLicenses = 1;
      if (values.licenses < team.activeUsers) {
        licenseIssues.push(` the number of users (${team.activeUsers})`);
        requiredLicenses = Math.max(requiredLicenses, team.activeUsers);
      }

      if (values.licenses < Math.ceil(team.activeChildConnections / 3)) {
        licenseIssues.push(` the number of connected teams (${team.activeChildConnections})`);
        requiredLicenses = Math.max(requiredLicenses, Math.ceil(team.activeChildConnections / 3));
      }

      if (licenseIssues.length) {
        errors.licenses = `Cannot have less licenses than ${requiredLicenses}, due to `;
        errors.licenses += licenseIssues.join(' and ') + '.';
      }

      if (Object.values(errors).length) return errors;
      return undefined;
    },
    onSubmit: (values) => updateSettingsMutation({ licenses: values.licenses }).then(reload),
  });

  // Estimate cost (although not applicable for custom app tiers)
  let costUsd = 0;
  if (formProps.values.licenses === 1) {
    costUsd = 99;
  }
  if (formProps.values.licenses > 1) {
    costUsd = 50 + formProps.values.licenses * 49;
  }

  return (
    <div>
      <VoxelPanel paddingClass="p-6" className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-row gap-1 items-center w-44">
            <VoxelText variant="title3">
              {formProps.values.licenses} license{formProps.values.licenses > 1 ? 's' : ''}
            </VoxelText>
            {!team.customAppTier && (
              <VoxelText variant="form-caption">(${costUsd}/month)</VoxelText>
            )}
          </div>
          <div />
          <VoxelIconButton
            disabled={formProps.values.licenses <= 1}
            onClick={() => {
              formProps.setFieldValue('licenses', Math.max(1, formProps.values.licenses - 1));
            }}
            icon={<FontAwesomeIcon icon={faMinus} />}
          />

          <VoxelIconButton
            onClick={() => formProps.setFieldValue('licenses', formProps.values.licenses + 1)}
            icon={<FontAwesomeIcon icon={faPlus} />}
          />
        </div>
        <VoxelText variant="form-caption">Increase capacity, users and connected teams</VoxelText>
        <div className="py-2" />
        <VoxelButton
          disabled={!formProps.dirty || !formProps.isValid || formProps.isSubmitting}
          title="Save"
          size="md"
          color="primary"
          endIcon={<FontAwesomeIcon icon={faSave} />}
          onClick={formProps.submitForm}
        />
        {!!formProps.errors?.licenses && (
          <VoxelAlert size="sm" severity="info">
            {formProps.errors.licenses}
          </VoxelAlert>
        )}
      </VoxelPanel>
      <Capacity team={team} licenses={formProps.values.licenses} />
    </div>
  );
};

export default LicenseSettings;
